import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect
} from 'react'
import * as d3 from 'd3'
import browsercheck from '@ta-interaktiv/browsercheck'
// eslint-disable-next-line
import Worker from 'worker-loader!./worker.js'
import { Municipality } from 'types/municipalityChart'

interface Props {
  width: number
  data: Array<Municipality>
  selectedMunicipalityNumber: number
}

export const MunicipalityChart = ({
  width,
  data,
  selectedMunicipalityNumber
}: Props) => {
  const chartWorker = useRef<Worker | null>(null)
  const [showLoading, setShowLoading] = useState(true)
  const [selectedMunicipality, setSelectedMunicipality] = useState<
    (Municipality & { vx: number; vy: number; x: number; y: number }) | null
  >(null)
  const heightRatio = browsercheck.isMobile ? 3 / 4 : 2 / 4
  const dpi = window.devicePixelRatio
  const loadingBarNode = useRef<HTMLDivElement | null>(null)
  const ctx = useRef<CanvasRenderingContext2D | null>(null)
  const margins = {
    left: 20,
    right: 20,
    top: 20,
    bottom: 40
  }
  const circleColor = d3
    .scaleLinear<string>()
    .domain([0, 0.499, 0.501, 1])
    .range(['#d3343f', '#d3343f', '#378ebd', '#378ebd'])
  const [dimensions, setDimensions] = useState<{
    width: number
    height: number
  }>({
    width: width * dpi,
    height: width * dpi * heightRatio
  })
  const graphWidth = dimensions.width - margins.left * dpi - margins.right * dpi
  const x = d3
    .scaleLinear()
    .range([0, graphWidth])
    .domain([0, 1])

  const xNo = d3
    .scaleLinear()
    .range([0, graphWidth / 2])
    .domain([0, 0.5])

  const xYes = d3
    .scaleLinear()
    .range([graphWidth / 2, graphWidth])
    .domain([0.5, 1])

  const yScale = d3
    .scaleLinear()
    .range([0, dimensions.height - margins.top * dpi - margins.bottom * dpi])
    .domain([0, 500])

  const radius = browsercheck.isMobile ? 6 : 3

  // When the worker is building the simulation
  // on each tick, make the loading bar wider
  const ticked = (data: { type: string; progress: number }) => {
    const progress = data.progress
    if (loadingBarNode && loadingBarNode.current) {
      loadingBarNode.current.style.width = 100 * progress + '%'
    }
  }

  // When the simulation has ended building the graph
  const ended = (data: {
    type: string
    nodes: Array<
      Municipality & { vx: number; vy: number; x: number; y: number }
    >
    bins: any
  }) => {
    // Remove the loading bar
    setShowLoading(false)

    // Draw
    if (ctx && ctx.current) {
      // clear the canvas
      ctx.current.clearRect(0, 0, dimensions.width, dimensions.height)

      // Draw the NO bins
      data.bins.no.forEach(bin => {
        ctx.current.fillStyle = circleColor(0)
        ctx.current.fillRect(
          margins.left * dpi + xNo(bin.x0),
          dimensions.height - 45 * dpi,
          xNo(bin.x1) - xNo(bin.x0) - 1,
          -yScale(bin.length)
        )
      })

      // Draw the YES bins
      data.bins.yes.forEach(bin => {
        ctx.current.fillStyle = circleColor(1)
        ctx.current.fillRect(
          margins.left * dpi + xYes(bin.x0),
          dimensions.height - 45 * dpi,
          xYes(bin.x1) - xYes(bin.x0) - 1,
          -yScale(bin.length)
        )
      })
    }

    // Set the selected bin
    let selectedBin = data.bins.no.find(bin =>
      bin.find(
        municipality =>
          +municipality.municipalityNumber === +selectedMunicipalityNumber
      )
    )
    let scale = xNo
    if (!selectedBin) {
      selectedBin = data.bins.yes.find(bin =>
        bin.find(
          municipality =>
            +municipality.municipalityNumber === +selectedMunicipalityNumber
        )
      )
      scale = xYes
    }

    const s = data.nodes.find(
      n => +n.municipalityNumber === +selectedMunicipalityNumber
    )

    if (selectedBin) {
      const xpos =
        scale(selectedBin.x0) +
        (scale(selectedBin.x1) - scale(selectedBin.x0)) / 2

      setSelectedMunicipality({
        ...s,
        x: margins.left * dpi + xpos,
        y: dimensions.height - 45 * dpi - yScale(selectedBin.length),
        n: selectedBin.length
      })
    }
  }

  const init = () => {
    setShowLoading(true)
    // https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers
    // https://webpack.js.org/loaders/worker-loader/
    // https://stackoverflow.com/questions/50778655/how-to-use-web-workers-in-create-react-app-with-worker-loader
    if (chartWorker && chartWorker.current) {
      chartWorker.current.terminate()
    }

    chartWorker.current = new Worker()

    chartWorker.current.postMessage({
      nodes: data,
      dimensions: dimensions,
      margins: margins,
      radius: radius,
      dpi: dpi
    })

    chartWorker.current.onmessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case 'tick':
          return ticked(event.data)
        case 'end':
          return ended(event.data)
      }
    }
  }

  // Get the context when the canvas is in the DOM and initialize the simulation
  const canvas = useCallback(node => {
    if (node) {
      ctx.current = node.getContext('2d')
    }
  }, [])

  const loadingBar = useCallback(node => {
    loadingBarNode.current = node
  }, [])

  // Update the dimensions state when the props has been changed
  useEffect(() => {
    setDimensions({
      width: width * dpi,
      height: width * dpi * heightRatio
    })
  }, [width])

  // Redraw when the dimensions have changed
  useLayoutEffect(() => {
    if (dimensions.width > 0) {
      init()
    }
  }, [dimensions.width, selectedMunicipalityNumber, data])

  const selectedMunicipalityPct = selectedMunicipality
    ? selectedMunicipality.result < 0.5
      ? Math.round(Math.abs(1 - selectedMunicipality.result) * 1000) / 10
      : Math.round(selectedMunicipality.result * 1000) / 10
    : 0

  const position = {
    left: selectedMunicipality
      ? selectedMunicipality.x + margins.left * dpi < dimensions.width / 2
      : 0,
    top: selectedMunicipality
      ? selectedMunicipality.y + margins.top * dpi < dimensions.height / 2
      : 0
  }

  return (
    <>
      {showLoading && (
        <div className='loading-screen'>
          <div className='loading-bar' ref={loadingBar} />
        </div>
      )}
      {selectedMunicipality && (
        <div>
          <div
            className={`municipality ${position.left ? 'left' : 'right'} ${
              position.top ? 'top' : 'bottom'
            }`}
            style={{
              left: selectedMunicipality.x / dpi,
              top: selectedMunicipality.y / dpi
            }}
          >
            <img src={require('../../images/arrow.svg')} />
            <div
              className={`histogram-label ${
                selectedMunicipality.result > 0.5 ? 'yes' : 'no'
              }`}
            >
              <div className='text-name'>
                {selectedMunicipality.municipalityName}
              </div>
              <div className='text-pct'>
                <b>{selectedMunicipalityPct}</b>%
              </div>
            </div>
          </div>
        </div>
      )}
      <canvas
        ref={canvas}
        width={dimensions.width}
        height={dimensions.height}
        style={{
          width: '100%',
          height: 'auto'
        }}
      />
    </>
  )
}
